import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import ExplicitBlur from "../components/ExplicitBlur";
import WaitlistForm from "../components/WaitlistForm";
import { useCart } from "../context/CartContext";
import { SizeGuide, PAPER_SIZES } from "../components/SizeGuide";

export default function PrintDetail() {
    const { slug } = useParams();
    const [print, setPrint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSize, setSelectedSize] = useState(null);
    const nav = useNavigate();
    const { add } = useCart();

    useEffect(() => {
        setLoading(true);
        api.get(`/prints/${slug}`).then((r) => {
            setPrint(r.data);
            const sizes = r.data.sizes || [];
            const first = sizes.find((s) => s.price > 0);
            if (first) setSelectedSize(first.label);
        }).catch(() => setPrint(null)).finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="pt-40 text-center text-[#5A5A5A] label-caps">Loading...</div>;
    if (!print) return (
        <div className="pt-40 text-center">
            <div className="font-serif-display text-3xl text-[#1A1A1A] mb-4">Print not found</div>
            <Link to="/gallery" className="text-[#5A5A5A] hover:text-[#1A1A1A] nav-link">← Back to gallery</Link>
        </div>
    );

    const sizes = (print.sizes || []).filter((s) => s.price > 0);
    const isOriginal = print.is_original;
    const isSold = print.is_sold;
    const activeSize = sizes.find((s) => s.label === selectedSize);
    const displayPrice = isOriginal ? print.price : (activeSize ? activeSize.price : print.price);

    const handleAdd = () => {
        if (isSold) return;
        if (!isOriginal && sizes.length && !activeSize) {
            toast.error("Please choose a size");
            return;
        }
        add({ ...print, price: displayPrice, size_label: isOriginal ? null : (activeSize?.label || null) });
        toast.success(`Added "${print.title}" to cart`);
    };

    const handleBuyNow = () => {
        if (isSold) return;
        if (!isOriginal && sizes.length && !activeSize) {
            toast.error("Please choose a size");
            return;
        }
        add({ ...print, price: displayPrice, size_label: isOriginal ? null : (activeSize?.label || null) });
        nav("/cart");
    };

    return (
        <div className="relative z-10 pt-24 sm:pt-28 pb-32 md:pb-24" data-testid="print-detail-page">
            <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16">
                <Link to={isOriginal ? "/originals" : "/gallery"} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5A5A] hover:text-[#1A1A1A] mb-6 sm:mb-10" data-testid="back-to-gallery">
                    <ArrowLeft className="w-3 h-3" /> Back to {isOriginal ? "Originals" : "Gallery"}
                </Link>

                <div className="grid md:grid-cols-2 lg:grid-cols-[1.4fr_1fr] gap-10 md:gap-10 lg:gap-20 items-start">
                    <div className="aspect-[3/4] bg-[#F0EDE5] overflow-hidden fade-up relative md:sticky md:top-28" data-testid="print-image-wrap">
                        <ExplicitBlur
                            isExplicit={print.is_explicit}
                            src={fileUrl(print.image_url || print.image_path)}
                            alt={print.title}
                            className="w-full h-full"
                            testId="print-detail-reveal"
                        />
                        {isSold && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid="print-sold-overlay">
                                <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px] mix-blend-luminosity" />
                                <div className="relative font-serif-display italic text-white/85 text-8xl sm:text-9xl tracking-[0.15em] select-none" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
                                    sold
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:sticky lg:top-32 fade-up">
                        <div className="label-caps text-[#8C3A3A] mb-4">
                            {print.medium}{isOriginal && " · One-of-One Original"}
                        </div>
                        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] font-light tracking-tighter leading-none mb-6 sm:mb-8">
                            {print.title}
                        </h1>

                        <div className="text-3xl font-serif-display text-[#1A1A1A] mb-8 sm:mb-10" data-testid="print-price">
                            A${displayPrice.toFixed(2)}
                        </div>

                        <p className="text-[#5A5A5A] leading-relaxed mb-10 text-base">{print.description}</p>

                        {!isOriginal && sizes.length > 0 && (
                            <div className="mb-10">
                                <div className="label-caps mb-4">Choose a size</div>
                                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3" data-testid="size-selector">
                                    {(print.sizes || []).map((s) => {
                                        const disabled = !s.price || s.price <= 0;
                                        const active = selectedSize === s.label;
                                        return (
                                            <button
                                                key={s.label}
                                                onClick={() => !disabled && setSelectedSize(s.label)}
                                                disabled={disabled}
                                                data-testid={`size-${s.label.toLowerCase()}`}
                                                className={`px-6 py-3 border text-xs tracking-[0.2em] uppercase transition-colors ${
                                                    disabled
                                                        ? "border-black/10 text-[#B0B0B0] cursor-not-allowed"
                                                        : active
                                                            ? "bg-[#1A1A1A] border-[#1A1A1A] text-[#F2F0E9]"
                                                            : "border-black/20 text-[#1A1A1A] hover:bg-black/5"
                                                }`}
                                            >
                                                {s.label} {disabled ? "· on request" : `· A$${s.price}`}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(print.sizes || []).some((s) => !s.price || s.price <= 0) && (
                                    <div className="mt-4 text-sm text-[#5A5A5A]" data-testid="size-on-request-note">
                                        Other sizes are available on request. <Link to="/commissions" className="text-[#1A1A1A] nav-link" data-testid="size-enquire-link">Enquire →</Link>
                                    </div>
                                )}
                                <SizeGuide sizes={print.sizes || []} active={selectedSize} />
                            </div>
                        )}

                        <div className="space-y-3 mb-10">
                            <div className="flex justify-between py-3 border-b border-black/[0.06]">
                                <span className="label-caps">Format</span>
                                <span className="text-sm text-[#1A1A1A]">{isOriginal ? "Original work on canvas/paper" : "Fine art print, giclée"}</span>
                            </div>
                            <div className="flex justify-between py-3 border-b border-black/[0.06]">
                                <span className="label-caps">Paper</span>
                                <span className="text-sm text-[#1A1A1A]">Hahnemühle 308gsm</span>
                            </div>
                            <div className="flex justify-between py-3 border-b border-black/[0.06]">
                                <span className="label-caps">Edition</span>
                                <span className="text-sm text-[#1A1A1A]">{isOriginal ? "One-of-one, signed" : "Limited, signed by artist"}</span>
                            </div>
                            <div className="flex justify-between py-3 border-b border-black/[0.06]">
                                <span className="label-caps">Shipping</span>
                                <span className="text-sm text-[#1A1A1A]">{isOriginal ? "Framed, insured worldwide" : "Worldwide, dropshipped"}</span>
                            </div>
                        </div>

                        {isSold ? (
                            <div className="border border-[#8C3A3A]/40 bg-[#8C3A3A]/5 p-6" data-testid="sold-block">
                                <div className="label-caps text-[#8C3A3A] mb-2">This original is sold</div>
                                <p className="text-sm text-[#5A5A5A] mb-4">Be first to hear about future drops and rereleases of similar work.</p>
                                <WaitlistForm source="sold_original" print_id={print.id} cta="Join list" />
                                <div className="mt-4 label-caps">
                                    Or <Link to="/commissions" className="text-[#1A1A1A] nav-link">commission a piece →</Link>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleAdd}
                                    className="flex-1 inline-flex items-center justify-center gap-3 whitespace-nowrap border border-black/20 text-[#1A1A1A] px-6 lg:px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors"
                                    data-testid="add-to-cart-btn"
                                >
                                    <ShoppingBag className="w-4 h-4" /> Add to Cart
                                </button>
                                <button
                                    onClick={handleBuyNow}
                                    className="flex-1 inline-flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] hover:text-white transition-colors"
                                    data-testid="buy-now-btn"
                                >
                                    Buy Now
                                </button>
                            </div>
                        )}

                        <div className="mt-8 label-caps text-[#8A8A8A]">
                            For commissions in similar style — <Link to="/commissions" className="text-[#5A5A5A] hover:text-[#1A1A1A] nav-link">inquire here</Link>
                        </div>
                    </div>
                </div>
            </div>
            {!isSold && (
                <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FAFAF7]/95 backdrop-blur-xl border-t border-black/[0.08] px-5 py-3 flex items-center gap-4" data-testid="mobile-buy-bar">
                    <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-xl text-[#1A1A1A] leading-tight" data-testid="mobile-buy-bar-price">A${displayPrice.toFixed(2)}</div>
                        <div className="label-caps truncate">{print.title}{activeSize ? ` · ${activeSize.label}${PAPER_SIZES[activeSize.label.toUpperCase()] ? ` (${PAPER_SIZES[activeSize.label.toUpperCase()]})` : ""}` : ""}</div>
                    </div>
                    <button onClick={handleAdd} className="p-3 border border-black/20 rounded-full text-[#1A1A1A]" aria-label="Add to cart" data-testid="mobile-add-to-cart-btn">
                        <ShoppingBag className="w-4 h-4" />
                    </button>
                    <button onClick={handleBuyNow} className="bg-[#1A1A1A] text-[#FAFAF7] px-6 py-3 rounded-full text-sm font-medium" data-testid="mobile-buy-now-btn">
                        Buy Now
                    </button>
                </div>
            )}
        </div>
    );
}
